import type {ReactNode} from 'react'
import {
  AlertFillIcon,
  CommentIcon,
  CopilotIcon,
  FileDiffIcon,
  GitMergeIcon,
  GitPullRequestDraftIcon,
  HourglassIcon,
  PeopleIcon,
  XCircleFillIcon,
} from '@primer/octicons-react'
import {isFailingOrIncompleteStatus} from '@github-ui/mergebox/page-data/payloads/status-checks'
import type {ConflictsSectionStatuses} from '@github-ui/mergebox/sections/ConflictsSection'
import {useMergeboxStatusCalculator} from '@github-ui/mergebox/use-mergebox-status-calculator'
import type {ReviewRequestsData} from '../../../page-data/payloads/review-requests'
import {ActionItem} from './ActionItem'
import {ActionItemGroup} from './ActionItemGroup'
import type {ValidatedMergeStatusButtonData} from '../../../hooks/use-merge-status-button-data'

interface AuthorActionsProps {
  viewerLogin: string
  reviewers: ReviewRequestsData
  mergeStatusButtonData: ValidatedMergeStatusButtonData | undefined
}

interface AuthorActionsComponentProps {
  viewerLogin: string
  reviewers: ReviewRequestsData
  statusChecksData: ReturnType<typeof useMergeboxStatusCalculator>['statusChecksData']
  calculator: ReturnType<typeof useMergeboxStatusCalculator>['calculator']
  pullRequest: ReturnType<typeof useMergeboxStatusCalculator>['pullRequest']
}

/**
 * Container component for author action items.
 * This component is responsible for fetching the necessary data and calculating which action items to show for the author of the pull request.
 * The actual rendering of the action items is delegated to the AuthorActionsComponent.
 */
export function AuthorActions({viewerLogin, reviewers, mergeStatusButtonData}: AuthorActionsProps) {
  const {calculator, pullRequest, statusChecksData} = useMergeboxStatusCalculator({
    headSha: mergeStatusButtonData?.headSha ?? '',
    channels: mergeStatusButtonData?.channels ?? null,
  })

  return (
    <AuthorActionsComponent
      viewerLogin={viewerLogin}
      reviewers={reviewers}
      statusChecksData={statusChecksData}
      calculator={calculator}
      pullRequest={pullRequest}
    />
  )
}

/**
 * Presentational component for author action items.
 * We separate this from the main AuthorActions component to isolate the logic for calculating which action items to
 * show from the data fetching and state management logic in the main AuthorActions component.
 */
export function AuthorActionsComponent({
  viewerLogin,
  reviewers,
  statusChecksData,
  calculator,
  pullRequest,
}: AuthorActionsComponentProps) {
  const statusChecks = statusChecksData.statusChecks
  const conflictsSection = calculator.sections.ConflictsSection
  const reviewerSection = calculator.sections.ReviewerSection

  const failingChecks = statusChecks.filter(c => isFailingOrIncompleteStatus(c.state))
  const failingRequiredChecks = failingChecks.filter(c => c.isRequired)
  const failingOptionalChecks = failingChecks.filter(c => !c.isRequired)

  const reviewsWithUnresolved = reviewers.completedReviews
    .map(review => ({
      login: review.reviewer.displayLogin,
      unresolved: review.threadCounts.total - review.threadCounts.resolved,
    }))
    .filter(r => r.unresolved > 0)

  const copilotUnresolved = reviewsWithUnresolved.filter(r => r.login === 'Copilot')
  const humanUnresolved = reviewsWithUnresolved.filter(r => r.login !== 'Copilot')

  const copilotUnresolvedCount = copilotUnresolved.reduce((sum, r) => sum + r.unresolved, 0)

  const hasReviewers = reviewers.pendingReviewRequests.length > 0 || reviewers.completedReviews.length > 0
  const hasSuggestedReviewers = reviewers.suggestedReviewers.length > 0

  const items: ReactNode[] = []

  if (pullRequest.isDraft) {
    const hasOutstandingIssues =
      failingRequiredChecks.length > 0 ||
      hasConflicts(conflictsSection) ||
      reviewerSection.sectionStatus === 'CHANGES_REQUESTED'

    const description = hasOutstandingIssues
      ? 'Address outstanding issues before marking as ready for review.'
      : 'No outstanding issues. This pull request may be ready for review.'

    items.push(
      <ActionItem
        key="draft"
        leadingIcon={GitPullRequestDraftIcon}
        title="Mark as ready for review"
        description={description}
        action={{panel: 'mergeStatus', label: 'Ready for review'}}
      />,
    )
  }

  if (hasConflicts(conflictsSection)) {
    const conflictDescription = getConflictDescription(conflictsSection.sectionStatus)
    items.push(
      <ActionItem
        key="merge-conflicts"
        leadingIcon={AlertFillIcon}
        title="Merge conflicts must be resolved"
        variant="warning"
        description={conflictDescription}
        action={{panel: 'mergeStatus', label: 'Resolve conflicts'}}
      />,
    )
  }

  if (reviewerSection.shouldRender && reviewerSection.sectionStatus === 'CHANGES_REQUESTED') {
    const changesRequestedReviews = reviewers.completedReviews.filter(r => r.state === 'changes_requested')

    items.push(
      <ActionItemGroup
        key="changes-requested"
        title="Changes requested"
        count={changesRequestedReviews.length}
        items={changesRequestedReviews.map(review => ({
          leadingIcon: FileDiffIcon,
          variant: 'danger',
          title: `@${review.reviewer.displayLogin} requested changes`,
          action: {
            panel: 'comments',
            label: 'View feedback',
            metadata: {selectedAuthor: review.reviewer.displayLogin, hideResolved: true},
          },
        }))}
      />,
    )
  }

  if (reviewerSection.shouldRender && reviewerSection.sectionStatus === 'REVIEW_REQUIRED') {
    const reviewTitle =
      reviewerSection.numReviewsRequired > 0
        ? `Waiting for ${reviewerSection.numReviewsRequired} required review${reviewerSection.numReviewsRequired === 1 ? '' : 's'}`
        : 'Waiting for review'
    items.push(
      <ActionItem
        key="waiting-for-review"
        leadingIcon={HourglassIcon}
        title={reviewTitle}
        description="No action needed — waiting for reviewers to submit their feedback."
      />,
    )
  }

  if (failingRequiredChecks.length > 0) {
    items.push(
      <ActionItemGroup
        key="failing-required-checks"
        title="Fix required failing checks"
        count={failingRequiredChecks.length}
        action={{panel: 'mergeStatus', label: 'View checks'}}
        items={failingRequiredChecks.map(check => ({
          leadingIcon: XCircleFillIcon,
          title: check.displayName,
          description: check.description,
          variant: 'danger',
        }))}
      />,
    )
  }

  if (failingOptionalChecks.length > 0) {
    items.push(
      <ActionItem
        key="failing-checks"
        leadingIcon={XCircleFillIcon}
        variant="warning"
        title={`${failingOptionalChecks.length} optional check${failingOptionalChecks.length === 1 ? '' : 's'} failing`}
        action={{panel: 'mergeStatus', label: 'View checks'}}
      />,
    )
  }

  if (copilotUnresolvedCount > 0) {
    items.push(
      <ActionItem
        key="copilot-unresolved-comments"
        leadingIcon={CopilotIcon}
        title={`${copilotUnresolvedCount} unresolved Copilot comment${copilotUnresolvedCount === 1 ? '' : 's'}`}
        description="Review and resolve comments from Copilot code review."
        action={{
          panel: 'comments',
          label: 'View comments',
          metadata: {selectedAuthor: 'Copilot', hideResolved: true},
        }}
      />,
    )
  }

  if (humanUnresolved.length > 0) {
    const humanUnresolvedCount = humanUnresolved.reduce((sum, r) => sum + r.unresolved, 0)

    items.push(
      <ActionItemGroup
        key="unresolved-comments"
        title="Unresolved comments"
        count={humanUnresolvedCount}
        items={humanUnresolved.map(r => ({
          leadingIcon: CommentIcon,
          title: `${r.unresolved} comment${r.unresolved === 1 ? '' : 's'} from @${r.login}`,
          action: {
            panel: 'comments',
            label: 'View comments',
            metadata: {selectedAuthor: r.login, hideResolved: true},
          },
        }))}
      />,
    )
  }

  if (!hasReviewers && hasSuggestedReviewers) {
    items.push(
      <ActionItemGroup
        key="add-reviewers"
        title="Add suggested reviewers"
        count={reviewers.suggestedReviewers.length}
        items={reviewers.suggestedReviewers.map(({reviewer, description}) => ({
          leadingIcon: PeopleIcon,
          title: reviewer.displayLogin,
          description,
          action: {
            onClick: () => {
              /* TODO: wire up add reviewer */
            },
            label: 'Add reviewer',
          },
        }))}
      />,
    )
  }

  if (items.length === 0) {
    items.push(
      <ActionItem
        key="no-action-items"
        leadingIcon={GitMergeIcon}
        title={`No action items for @${viewerLogin}`}
        description="This PR may be ready to merge! Review the pull request details and merge when ready."
        action={{panel: 'mergeStatus', label: 'Ready to merge'}}
      />,
    )
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-redundant-roles
    <ul role="list" className="d-flex flex-column gap-2">
      {items}
    </ul>
  )
}

type ConflictsSectionData = ReturnType<typeof useMergeboxStatusCalculator>['calculator']['sections']['ConflictsSection']

function hasConflicts(section: ConflictsSectionData): boolean {
  return (
    section.shouldRender &&
    (section.sectionStatus === 'HAS_CONFLICTS' || section.sectionStatus === 'HAS_REBASE_CONFLICTS')
  )
}

function getConflictDescription(status: ConflictsSectionStatuses): string {
  if (status === 'HAS_REBASE_CONFLICTS') {
    return 'This branch has rebase conflicts that must be resolved locally.'
  }
  return 'This branch has conflicts that must be resolved.'
}
